import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Calendar, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { useLocalize } from '@/hooks/useLandingContent';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { estimateReadingTime } from '@/lib/readingTime';

const FALLBACK_CATEGORIES = [
  'National', 'International', 'Business', 'Real Estate',
  'Legal-Taxation', 'Notices-Announcements', 'New-Developments', 'Construction',
];
const PAGE_SIZE = 12;

export default function News() {
  const localize = useLocalize();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const urlCategory = searchParams.get('category') || 'ALL';
  const urlTag = searchParams.get('tag') || '';
  const [activeCat, setActiveCat] = useState(urlCategory);

  useEffect(() => { setActiveCat(urlCategory); setPage(1); }, [urlCategory, urlTag]);

  const { data: cats = [] } = useQuery({
    queryKey: ['news-categories-public'],
    queryFn: async () => {
      const { data } = await supabase
        .from('news_categories')
        .select('slug, name, is_active, sort_order')
        .eq('is_active', true)
        .order('sort_order');
      return data || [];
    },
  });
  const localizeMaybe = useLocalize();
  const categoryNames = cats.length
    ? cats.map((c: any) => localizeMaybe(c.name) || c.slug)
    : FALLBACK_CATEGORIES;
  const CATEGORIES = ['ALL', ...categoryNames];

  const { data, isLoading } = useQuery({
    queryKey: ['news-list', page, urlCategory, urlTag],
    queryFn: async () => {
      let q = supabase
        .from('news_articles')
        .select('*', { count: 'exact' })
        .eq('is_published', true);
      if (urlCategory && urlCategory !== 'ALL') q = q.eq('category', urlCategory);
      if (urlTag) q = q.contains('tags', [urlTag]);
      const { data, count } = await q
        .order('is_pinned', { ascending: false })
        .order('is_featured', { ascending: false })
        .order('published_at', { ascending: false })
        .range(0, page * PAGE_SIZE - 1);
      return { items: data || [], total: count || 0 };
    },
  });

  const items = data?.items || [];
  const fmtDate = (s?: string | null) =>
    s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const readTime = (body: any) => estimateReadingTime(body);
  const pickCategory = (item: any, i: number) =>
    item?.category || CATEGORIES[(i % (CATEGORIES.length - 1)) + 1];

  const setCategory = (c: string) => {
    const next = new URLSearchParams(searchParams);
    if (c === 'ALL') next.delete('category'); else next.set('category', c);
    setSearchParams(next, { replace: true });
  };

  return (
    <Layout>
      <SEOHead
        title="Real Estate News — Ethio Real Estates"
        description="Latest news on Ethiopia's property market, government policies, mega-projects, and developer launches."
        canonical="/news"
      />

      {/* Category bar */}
      <div className="border-b bg-card">
        <div className="container overflow-x-auto">
          <div className="flex items-center gap-1 py-2 min-w-max">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 text-xs font-semibold whitespace-nowrap border-b-2 transition ${
                  activeCat === c
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="bg-gradient-to-b from-muted/40 to-background py-12 border-b">
        <div className="container text-center">
          <div className="text-xs font-semibold tracking-[0.25em] text-primary uppercase mb-3">Our News</div>
          <h1 className="font-display text-3xl md:text-5xl font-bold mb-4">Find our all news from here</h1>
          <p className="max-w-2xl mx-auto text-sm text-muted-foreground">
            Our news is researched and written by trusted reporters covering policy, infrastructure, and the property
            market across Ethiopia.
          </p>
        </div>
      </section>

      <section className="container py-10">
        {isLoading ? (
          <div className="text-center py-16 text-muted-foreground">Loading news…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">No news yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {items.map((n: any, idx: number) => (
              <article
                key={n.id}
                onClick={() => (window.location.href = `/news/${n.slug || n.id}`)}
                className="rounded-xl overflow-hidden bg-card border hover:shadow-lg transition group cursor-pointer"
              >
                <div className="relative aspect-[16/11] bg-muted overflow-hidden">
                  {n.cover_url && (
                    <img
                      src={optimizedImageUrl(n.cover_url, { width: 500, height: 340, quality: 75 })}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  )}
                  <span className="absolute top-2 left-2 px-2.5 py-1 rounded-full bg-card/95 text-[10px] font-semibold backdrop-blur">
                    {pickCategory(n, idx)}
                  </span>
                </div>
                <div className="p-4">
                  <h2 className="font-bold text-sm leading-snug line-clamp-2 mb-2 group-hover:text-primary">
                    {localize(n.title)}
                  </h2>
                  <p className="text-xs text-muted-foreground line-clamp-4 mb-3">{localize(n.summary)}</p>
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground border-t pt-2">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(n.published_at)}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {readTime(n.body)} min read</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {data && items.length < data.total && (
          <div className="text-center mt-10">
            <Button variant="outline" onClick={() => setPage((p) => p + 1)}>
              Load more news
            </Button>
          </div>
        )}
      </section>
    </Layout>
  );
}
